import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ArrowUp, Square, Paperclip, MousePointerClick, MessageSquareDiff, X, FolderIcon, Upload, Loader2, Settings, Sparkles, Pencil, Code } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import type { Conversation, OptimisticAttachment } from '@/types/conversations'
import { FilePreview } from './FilePreview'
import { useFileUploadWithDirectUpload } from '@/hooks/useFileUploadWithDirectUpload'
import { AssetPicker } from '@/components/assets/AssetPicker'
import type { Asset } from '@/types/assets'

interface SelectedElementContext {
  selector?: string
  cayuSelector?: string
  tag?: string
  text?: string
  route?: string
  appType?: 'web' | 'mobile'
  source?: 'canvas' | 'preview'
  elements?: Array<{ id: string; type: string; text?: string }>
}

interface MessageInputProps {
  conversation: Conversation
  onSendMessage: (content: string, signedBlobIds?: string[], selectedElementContext?: SelectedElementContext, optimisticAttachments?: OptimisticAttachment[]) => Promise<void>
  onToggleMode: () => void
  onSwitchToMode?: (mode: string) => Promise<void>
  onInterrupt?: () => Promise<void>
  disabled: boolean
  isSending: boolean
  isProcessing?: boolean
  isInterrupting?: boolean
  isWorkflowInProgress?: boolean
  pendingContent?: string
  onPendingContentConsumed?: () => void
  productSpec?: string
  isSelectionModeActive?: boolean
  onToggleSelectionMode?: () => void
  selectedElement?: SelectedElementContext | null
  onClearSelectedElement?: () => void
  organizationId?: number
  projectId?: number
  isBrowserUseTesting?: boolean
  showModeSelector?: boolean
  requestChangesTask?: { id: number; title: string } | null
  onClearRequestChangesTask?: () => void
}

export function MessageInput({
  conversation,
  onSendMessage,
  onToggleMode: _onToggleMode,
  onSwitchToMode,
  onInterrupt,
  disabled,
  isSending: _isSending,
  isProcessing = false,
  isInterrupting = false,
  isWorkflowInProgress = false,
  pendingContent,
  onPendingContentConsumed,
  productSpec: _productSpec,
  isSelectionModeActive = false,
  onToggleSelectionMode,
  selectedElement,
  onClearSelectedElement,
  organizationId,
  projectId,
  isBrowserUseTesting = false,
  showModeSelector = true,
  requestChangesTask,
  onClearRequestChangesTask,
}: MessageInputProps) {
  const { t } = useTranslation('conversations')
  const [content, setContent] = useState('')
  const [showAssetPicker, setShowAssetPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Get shared Inertia data
  const { props: pageProps } = usePage<{ config: { maxMessageLength: number } }>()
  const MAX_MESSAGE_LENGTH = pageProps.config.maxMessageLength

  // Handle pending content from suggestions
  useEffect(() => {
    if (pendingContent) {
      setContent(pendingContent)
      textareaRef.current?.focus()
      onPendingContentConsumed?.()
    }
  }, [pendingContent, onPendingContentConsumed])

  const {
    selectedFiles,
    uploadedFiles,
    uploadProgress,
    isDragging,
    fileInputRef,
    handleFileSelect,
    removeFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    clearFiles,
    setFiles,
    isUploading,
    allFilesUploaded,
    hasFailedUploads,
    retryUpload,
    getFileKey,
    addPreUploadedFile
  } = useFileUploadWithDirectUpload()

  // Auto-focus textarea on mount
  useEffect(() => {
    if (conversation.can_accept_messages) {
      textareaRef.current?.focus()
    }
  }, [conversation.can_accept_messages])

  // Auto-focus textarea when element is selected or task change requested
  useEffect(() => {
    if (selectedElement?.selector || selectedElement?.source === 'canvas') {
      textareaRef.current?.focus()
    }
  }, [selectedElement])

  useEffect(() => {
    if (requestChangesTask) {
      textareaRef.current?.focus()
    }
  }, [requestChangesTask])

  // Auto-resize textarea based on content (like ChatGPT)
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'
      // Set new height, capped at max
      const maxHeight = 200
      const newHeight = Math.min(textarea.scrollHeight, maxHeight)
      textarea.style.height = `${newHeight}px`
    }
  }, [content])

  const handleSend = async () => {
    const trimmedContent = content.trim()
    if (!trimmedContent || !conversation.can_accept_messages) return

    // Validate message length
    if (trimmedContent.length > MAX_MESSAGE_LENGTH) {
      toast.error(t('chat.messageTooLong'), {
        description: t('chat.messageTooLongDescription', { max: MAX_MESSAGE_LENGTH })
      })
      return
    }

    // Don't send if files are still uploading
    if (isUploading || (selectedFiles.length > 0 && !allFilesUploaded)) {
      toast.error(t('chat.toasts.waitForUpload'))
      return
    }

    // Check if any uploads failed
    if (hasFailedUploads) {
      toast.error(t('chat.toasts.uploadFailed'))
      return
    }

    // Clear form immediately (optimistic UI)
    const signedBlobIds = uploadedFiles.map(f => f.signedBlobId)
    const filesToRestore = selectedFiles
    const elementContextToSend = selectedElement

    // Create optimistic attachments using the LOCAL blob URL for instant preview
    // file.blobUrl = blob:http://... (instant, in browser memory)
    // file.url = /rails/active_storage/blobs/... (requires network fetch)
    const optimisticAttachments: OptimisticAttachment[] = uploadedFiles.map(file => ({
      filename: file.filename,
      content_type: file.contentType,
      byte_size: file.byteSize,
      previewUrl: file.blobUrl, // Local blob URL - instant, no network needed
      is_image: file.contentType.startsWith('image/')
    }))

    setContent('')
    clearFiles()
    if (selectedElement && onClearSelectedElement) {
      onClearSelectedElement()
    }

    // If request changes task is attached, prepend task context
    const taskContext = requestChangesTask
      ? `[Request changes for task: "${requestChangesTask.title}" (ID: ${requestChangesTask.id})]\n\n`
      : ''
    if (requestChangesTask && onClearRequestChangesTask) {
      onClearRequestChangesTask()
    }

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 10)

    try {
      await onSendMessage(
        taskContext + trimmedContent,
        signedBlobIds.length > 0 ? signedBlobIds : undefined,
        elementContextToSend || undefined,
        optimisticAttachments.length > 0 ? optimisticAttachments : undefined
      )
    } catch (error) {
      // On error, restore the message content and files
      setContent(trimmedContent)
      setFiles(filesToRestore)
      console.error('Failed to send message:', error)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!disabled && content.trim()) {
        handleSend()
      }
    }
  }

  const handleInterrupt = async () => {
    if (!onInterrupt || isInterrupting) return
    try {
      await onInterrupt()
    } catch (error) {
      console.error('Failed to interrupt processing:', error)
      toast.error(t('chat.toasts.stopFailed'))
    }
  }

  const handleAssetSelect = async (asset: Asset) => {
    if (!asset.signed_blob_id) {
      toast.error(t('chat.toasts.assetNotAvailable'))
      return
    }

    addPreUploadedFile({
      name: asset.name,
      contentType: asset.content_type,
      byteSize: asset.byte_size,
      signedBlobId: asset.signed_blob_id,
      previewUrl: asset.thumbnail_url ?? undefined
    })
  }

  return (
    <div
      className={`@container rounded-xl bg-content-card p-4 mb-2 mx-2 relative transition-all ${
        isDragging
          ? 'border-2 border-dashed border-primary'
          : 'border border-solid'
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-primary/10 rounded-xl flex items-center justify-center z-10 pointer-events-none animate-in fade-in duration-200">
          <div className="text-center space-y-1">
            <div className="inline-block p-2 bg-primary/10 rounded-full">
              <Paperclip className="h-6 w-6 text-primary" />
            </div>
            <p className="text-xs font-medium text-primary">{t('chat.dropFilesHere')}</p>
            <p className="text-[11px] text-muted-foreground">{t('chat.dropFilesDescription')}</p>
          </div>
        </div>
      )}

      {/* File Preview */}
      {selectedFiles.length > 0 && (
        <div className="mb-3">
          <FilePreview
            files={selectedFiles}
            uploadProgress={uploadProgress}
            uploadedFiles={uploadedFiles}
            onRemove={removeFile}
            onRetry={retryUpload}
            getFileKey={getFileKey}
          />
        </div>
      )}

      {/* Selected Element Badge */}
      {selectedElement && (selectedElement.selector || selectedElement.source === 'canvas') && (
        <div className="mb-2">
          <Badge variant="secondary" className="gap-1.5 py-1.5 px-2.5">
            <MousePointerClick className="h-3.5 w-3.5" />
            <span className="text-xs max-w-[300px] truncate" title={
              selectedElement.source === 'canvas'
                ? `${selectedElement.elements?.length} canvas element(s)`
                : t('chat.technicalSelector', { selector: selectedElement.selector })
            }>
              {selectedElement.source === 'canvas'
                ? (() => {
                    const els = selectedElement.elements || []
                    if (els.length === 1) {
                      const el = els[0]
                      return el.text
                        ? (el.text.length > 50 ? el.text.slice(0, 47) + '...' : el.text)
                        : el.type
                    }
                    return `${els.length} elements`
                  })()
                : (() => {
                    const text = selectedElement.text?.trim()
                    if (text && text.length > 0) {
                      return text.length > 50 ? `${text.slice(0, 47)}...` : text
                    }
                    return `<${selectedElement.tag}>`
                  })()
              }
            </span>
            {onClearSelectedElement && (
              <button
                onClick={onClearSelectedElement}
                className="ml-1 hover:bg-muted rounded-sm p-0.5 transition-colors"
                title={t('chat.clearSelection')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        </div>
      )}

      {/* Request Changes Task Badge */}
      {requestChangesTask && (
        <div className="mb-2">
          <Badge variant="secondary" className="gap-1.5 py-1.5 px-2.5">
            <MessageSquareDiff className="h-3.5 w-3.5" />
            <span className="text-xs max-w-[300px] truncate">
              {requestChangesTask.title}
            </span>
            {onClearRequestChangesTask && (
              <button
                onClick={onClearRequestChangesTask}
                className="ml-1 hover:bg-muted rounded-sm p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        </div>
      )}

      {/* Textarea */}
      <Textarea
        ref={textareaRef}
        data-chat-input
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('chat.placeholder')}
        disabled={!conversation.can_accept_messages}
        className="min-h-[40px] resize-none border-0 bg-transparent px-2 py-0 text-base placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none overflow-y-auto"
        style={{ maxHeight: '200px' }}
      />

      {/* Character Counter — always reserves space to prevent layout shift */}
      <div className={`text-[11px] px-2 mt-1 ${
        content.length === 0
          ? 'invisible'
          : content.length > MAX_MESSAGE_LENGTH
            ? 'text-destructive font-medium'
            : 'text-muted-foreground'
      }`}>
        {content.length > 0 ? `${content.length} / ${MAX_MESSAGE_LENGTH} ${t('chat.characters')}` : '\u00A0'}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.svg,.pdf,.docx,.txt,.md,.html,.jsx,.csv,.json,.xlsx,.xls"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between mt-1 gap-1">
        {/* Left side - Attach, Model, Mode */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* File Upload Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-xl text-muted-foreground hover:text-foreground"
                title={t('chat.attachFiles')}
                disabled={!conversation.can_accept_messages}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" />
                {t('chat.uploadFromComputer')}
              </DropdownMenuItem>
              {organizationId && (
                <DropdownMenuItem onClick={() => setShowAssetPicker(true)}>
                  <FolderIcon className="mr-2 h-4 w-4" />
                  {t('chat.chooseFromAssets')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mode Selector */}
          {onSwitchToMode && showModeSelector && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 px-2 rounded-xl text-muted-foreground hover:text-foreground"
                      disabled={!conversation.can_accept_messages}
                    >
                      <Settings className="h-4 w-4" />
                      <span className="text-xs font-medium hidden @[400px]:inline">
                        {conversation.preferred_mode === null && t('chat.modeAuto')}
                        {conversation.preferred_mode === 'prototype' && t('preview.prototype')}
                        {conversation.preferred_mode === 'implementation' && t('chat.modeBuild')}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('chat.modeTooltip')}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem
                  onClick={() => onSwitchToMode('discussion')}
                  className={conversation.preferred_mode === null ? 'bg-accent' : ''}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{t('chat.modeAuto')}</span>
                    <span className="text-xs text-muted-foreground">{t('chat.modeAutoDescription')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onSwitchToMode('prototype')}
                  className={conversation.preferred_mode === 'prototype' ? 'bg-accent' : ''}
                  disabled={isBrowserUseTesting}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{t('preview.prototype')}</span>
                    <span className="text-xs text-muted-foreground">{t('chat.modePrototypeDescription')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onSwitchToMode('implementation')}
                  className={conversation.preferred_mode === 'implementation' ? 'bg-accent' : ''}
                >
                  <Code className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{t('chat.modeBuild')}</span>
                    <span className="text-xs text-muted-foreground">{t('chat.modeBuildDescription')}</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Right side — Select Element, Send */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Select Element Button */}
          {onToggleSelectionMode && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 gap-1.5 px-2 rounded-xl ${
                    isSelectionModeActive
                      ? 'text-primary bg-primary/10 hover:text-primary hover:bg-primary/20'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  disabled={!conversation.can_accept_messages}
                  onClick={onToggleSelectionMode}
                >
                  <MousePointerClick className="h-4 w-4" />
                  <span className="text-sm hidden @[400px]:inline">{isSelectionModeActive ? t('chat.cancel') : t('chat.select')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isSelectionModeActive ? t('chat.cancelSelection') : t('chat.selectElement')}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Send/Stop Button - don't show stop button during workflow (workflow has its own abort) */}
          {isProcessing && !isWorkflowInProgress ? (
            <Button
              size="icon"
              className="h-9 w-9 rounded-full"
              disabled={isInterrupting}
              onClick={handleInterrupt}
              title={isInterrupting ? t('chat.stopping') : t('chat.stopProcessing')}
            >
              {isInterrupting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
            </Button>
          ) : (
            <Button
              size="icon"
              className="h-9 w-9 rounded-full"
              disabled={
                disabled ||
                !content.trim() ||
                content.length > MAX_MESSAGE_LENGTH ||
                isUploading ||
                (selectedFiles.length > 0 && !allFilesUploaded) ||
                hasFailedUploads
              }
              onClick={handleSend}
              title={
                isUploading || (selectedFiles.length > 0 && !allFilesUploaded)
                  ? t('chat.uploadingFiles')
                  : hasFailedUploads
                  ? t('chat.failedUploads')
                  : content.length > MAX_MESSAGE_LENGTH
                  ? t('chat.messageTooLong')
                  : t('chat.sendMessage')
              }
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Asset Picker Dialog */}
      {organizationId && (
        <AssetPicker
          open={showAssetPicker}
          onOpenChange={setShowAssetPicker}
          organizationId={organizationId}
          projectId={projectId}
          onAssetSelect={handleAssetSelect}
        />
      )}
    </div>
  )
}
